// =====================================================================
// Employee feature — statutory tables, role/designation defaults, and
// pure helper functions (PT/LWF/TDS/formatting).
//
// Extracted from pages/Employees.tsx without any logic changes.
// =====================================================================

// ==================== STATUTORY DATA TABLES ====================

// Professional Tax — state-wise monthly slabs (FY 2025-26)
export const PT_STATES: Record<
  string,
  { label: string; slabs: number[][]; annual: boolean; note?: string }
> = {
  karnataka: { label: "Karnataka", slabs: [[0, 14999, 0], [15000, 99999999, 200]], annual: false },
  maharashtra: { label: "Maharashtra", slabs: [[0, 7500, 0], [7501, 10000, 175], [10001, 99999999, 200]], annual: false },
  westbengal: { label: "West Bengal", slabs: [[0, 8500, 0], [8501, 10000, 90], [10001, 15000, 110], [15001, 25000, 130], [25001, 40000, 150], [40001, 99999999, 200]], annual: false },
  telangana: { label: "Telangana", slabs: [[0, 14999, 0], [15000, 20000, 150], [20001, 99999999, 200]], annual: false },
  tamilnadu: { label: "Tamil Nadu", slabs: [[0, 3500, 0], [3501, 5000, 22], [5001, 7500, 33], [7501, 10000, 52], [10001, 12500, 63], [12501, 15000, 83], [15001, 20000, 104], [20001, 25000, 135], [25001, 99999999, 182]], annual: false },
  gujarat: { label: "Gujarat", slabs: [[0, 5999, 0], [6000, 8999, 80], [9000, 11999, 150], [12000, 99999999, 200]], annual: false },
  kerala: { label: "Kerala", slabs: [[0, 99999999, 208]], annual: false },
  andhra: { label: "Andhra Pradesh", slabs: [[0, 14999, 0], [15000, 20000, 150], [20001, 99999999, 200]], annual: false },
  mp: { label: "Madhya Pradesh", slabs: [[0, 18749, 0], [18750, 25000, 125], [25001, 33333, 167], [33334, 99999999, 208]], annual: false },
  assam: { label: "Assam", slabs: [[0, 9999, 0], [10000, 14999, 150], [15000, 24999, 180], [25000, 99999999, 208]], annual: false },
  bihar: { label: "Bihar", slabs: [[0, 99999999, 0]], annual: false, note: "No PT in Bihar" },
  delhi: { label: "Delhi", slabs: [[0, 99999999, 0]], annual: false, note: "No PT in Delhi" },
  rajasthan: { label: "Rajasthan", slabs: [[0, 99999999, 0]], annual: false, note: "No PT in Rajasthan" },
  none: { label: "Not Applicable", slabs: [[0, 99999999, 0]], annual: false },
};

// Labour Welfare Fund — state-wise
export const LWF_STATES: Record<
  string,
  { label: string; ee: number; er: number; freq: string }
> = {
  andhra: { label: "Andhra Pradesh", ee: 20, er: 40, freq: "annual" },
  delhi: { label: "Delhi", ee: 0.75, er: 2.25, freq: "monthly" },
  gujarat: { label: "Gujarat", ee: 6, er: 12, freq: "halfyearly" },
  haryana: { label: "Haryana", ee: 25, er: 50, freq: "monthly" },
  karnataka: { label: "Karnataka", ee: 20, er: 40, freq: "annual" },
  kerala: { label: "Kerala", ee: 4, er: 8, freq: "halfyearly" },
  madhyapradesh: { label: "Madhya Pradesh", ee: 10, er: 20, freq: "halfyearly" },
  maharashtra: { label: "Maharashtra", ee: 25, er: 75, freq: "halfyearly" },
  odisha: { label: "Odisha", ee: 10, er: 20, freq: "annual" },
  punjab: { label: "Punjab", ee: 10, er: 20, freq: "monthly" },
  rajasthan: { label: "Rajasthan", ee: 25, er: 50, freq: "monthly" },
  tamilnadu: { label: "Tamil Nadu", ee: 10, er: 20, freq: "halfyearly" },
  telangana: { label: "Telangana", ee: 20, er: 40, freq: "annual" },
  westbengal: { label: "West Bengal", ee: 3, er: 15, freq: "monthly" },
  none: { label: "Not Applicable", ee: 0, er: 0, freq: "monthly" },
};

// TAX COMPUTATION FY 2025-26 (AY 2026-27)

// New Tax Regime slabs (Budget 2025)
export function taxNew(taxable: number): number {
  const slabs: number[][] = [
    [0, 400000, 0],
    [400001, 800000, 0.05],
    [800001, 1200000, 0.10],
    [1200001, 1600000, 0.15],
    [1600001, 2000000, 0.20],
    [2000001, 2400000, 0.25],
    [2400001, Infinity, 0.30],
  ];
  let tax = 0;
  for (const [lo, hi, rate] of slabs) {
    if (taxable <= lo) break;
    tax += (Math.min(taxable, hi) - lo) * rate;
  }
  // Rebate u/s 87A: zero tax (rebate up to ₹60,000) if taxable ≤ ₹12,00,000.
  if (taxable <= 1200000) {
    tax = Math.max(0, tax - Math.min(tax, 60000));
  } else {
    // Marginal relief: just above ₹12,00,000 the tax payable shall not
    // exceed the income that exceeds ₹12,00,000, so a small raise isn't
    // wiped out by tax. For higher incomes the slab tax is lower than the
    // excess, so Math.min leaves it unchanged.
    tax = Math.min(tax, taxable - 1200000);
  }
  return tax + tax * 0.04; // + 4% cess
}

// Old Tax Regime slabs (below 60 years)
export function taxOld(taxable: number): number {
  const slabs: number[][] = [
    [0, 250000, 0],
    [250001, 500000, 0.05],
    [500001, 1000000, 0.20],
    [1000001, Infinity, 0.30],
  ];
  let tax = 0;
  for (const [lo, hi, rate] of slabs) {
    if (taxable <= lo) break;
    tax += (Math.min(taxable, hi) - lo) * rate;
  }
  // Rebate u/s 87A: up to ₹12,500 if taxable ≤ ₹5,00,000
  if (taxable <= 500000) tax = Math.max(0, tax - Math.min(tax, 12500));
  return tax + tax * 0.04; // + 4% cess
}

export interface TaxDeclaration {
  sec80c?: number;
  sec80d?: number;
  sec80ccd1b?: number;
  hraExempt?: number;
  homeLoan?: number;
  ltaExempt?: number;
  sec80g?: number;
  npsEmployer?: number;
  ptPaid?: number;
  // Surfaced on the self-service Investment Declaration tab.
  sec80tta?: number; // Interest from savings deposits — max ₹10,000
  sec80eea?: number; // Interest on housing loan (first-time) — max ₹1,50,000
  otherDed?: number; // Any other deduction
}

// Annual taxable income after all deductions. Per company policy here,
// BOTH the employee's and the employer's PF contributions reduce taxable
// income in BOTH regimes (in addition to the standard deduction and, in
// the old regime, the declared chapter-VI-A deductions). PF is subtracted
// directly, so it is NOT also folded into the 80C cap.
export function calcTaxableIncome(
  grossAnnual: number,
  regime: string,
  declaration: TaxDeclaration,
  employeePFAnnual: number,
  employerPFAnnual: number = 0,
): number {
  const pfAnnual = (employeePFAnnual || 0) + (employerPFAnnual || 0);

  if (regime === "new") {
    const stdDeduction = 75000;
    const npsEmployerAnnual = (declaration.npsEmployer || 0) * 12;
    return Math.max(0, grossAnnual - stdDeduction - pfAnnual - npsEmployerAnnual);
  }

  // Old regime deductions
  const stdDeduction = 50000;
  const hraExemptAnnual = declaration.hraExempt || 0;
  const ltaExemptAnnual = declaration.ltaExempt || 0;
  const sec80cAnnual = Math.min(declaration.sec80c || 0, 150000);
  const sec80dAnnual = Math.min(declaration.sec80d || 0, 50000);
  const sec80ccd1bAnnual = Math.min(declaration.sec80ccd1b || 0, 50000);
  const homeLoanAnnual = Math.min(declaration.homeLoan || 0, 200000);
  const sec80gAnnual = declaration.sec80g || 0;
  const ptAnnual = (declaration.ptPaid || 0) * 12;
  const sec80ttaAnnual = Math.min(declaration.sec80tta || 0, 10000);
  const sec80eeaAnnual = Math.min(declaration.sec80eea || 0, 150000);
  const otherDedAnnual = declaration.otherDed || 0;

  return Math.max(
    0,
    grossAnnual - stdDeduction - pfAnnual - hraExemptAnnual - ltaExemptAnnual -
    sec80cAnnual - sec80dAnnual - sec80ccd1bAnnual - homeLoanAnnual - sec80gAnnual -
    ptAnnual - sec80ttaAnnual - sec80eeaAnnual - otherDedAnnual
  );
}

// Monthly income-tax (TDS) — slab tax + 87A rebate + marginal relief +
// 4% cess, divided by 12. Excludes PT/LWF (those are added separately by
// the caller). employerPFAnnual is optional for backward compatibility.
export function calcTDS(
  grossAnnual: number,
  regime: string,
  declaration: TaxDeclaration,
  employeePFAnnual: number,
  employerPFAnnual: number = 0,
): number {
  const taxableIncome = calcTaxableIncome(
    grossAnnual,
    regime,
    declaration,
    employeePFAnnual,
    employerPFAnnual,
  );
  const annualTax = regime === "new" ? taxNew(taxableIncome) : taxOld(taxableIncome);
  return Math.round(annualTax / 12);
}

// ==================== SALARY COMPONENT SPLIT ====================

// Standard CTC → monthly component breakup used by both the admin
// add/update employee form and the self-service Salary Declaration tab.
// Basic is fixed at 50% of monthly CTC, HRA at 50% of Basic; the
// remainder is spread as evenly as possible across the six allowances
// (extra ₹1 going to the earliest fields so the parts sum back to CTC).
export const SALARY_BASIC_PCT = 0.5; // Basic = 50% of CTC (floor)
export const SALARY_HRA_PCT = 0.5; // HRA = 50% of Basic (auto-split default)

export interface SalaryComponents {
  basic: number;
  hra: number;
  conveyance: number;
  medicalallowance: number;
  allowance: number;
  lta: number;
  others: number;
  bonusAmount: number;
}

export function splitCtcToComponents(monthlyCtc: number): SalaryComponents {
  const basic = Math.round(monthlyCtc * SALARY_BASIC_PCT);
  const hra = Math.round(basic * SALARY_HRA_PCT);

  const remaining = Math.round(monthlyCtc - basic - hra);
  const allowanceCount = 6; // conveyance, medical, special, lta, others, bonus
  const baseShare = Math.floor(remaining / allowanceCount);
  const leftover = remaining - baseShare * allowanceCount;
  const shares = Array.from({ length: allowanceCount }, (_, i) =>
    i < leftover ? baseShare + 1 : baseShare
  );
  const [conveyance, medicalallowance, allowance, lta, others, bonusAmount] = shares;

  return { basic, hra, conveyance, medicalallowance, allowance, lta, others, bonusAmount };
}

// Minimum allowed Basic for a given monthly CTC. The Salary Declaration
// tab lets an employee raise Basic but never go below the floor
// (Basic ≥ 50% CTC).
export function salaryFloors(monthlyCtc: number, basic: number) {
  const minBasic = Math.round(monthlyCtc * SALARY_BASIC_PCT);
  const minHra = Math.round((basic || minBasic) * SALARY_HRA_PCT);
  return { minBasic, minHra };
}

// House Rent Allowance tax exemption (old regime). The exempt amount is
// the least of three legal limits:
//   1. Actual HRA received (annual)
//   2. Rent paid (annual) minus 10% of Basic salary (annual)
//   3. 50% of Basic for metro cities (Delhi/Mumbai/Kolkata/Chennai),
//      else 40% of Basic for non-metro
// Returns 0 when the employee doesn't pay rent or receives no HRA.
export function calcHraExemption(
  basicAnnual: number,
  hraReceivedAnnual: number,
  rentPaidAnnual: number,
  isMetro: boolean,
): number {
  if (rentPaidAnnual <= 0 || hraReceivedAnnual <= 0) return 0;
  const cityCap = (isMetro ? 0.5 : 0.4) * basicAnnual;
  const rentMinusTen = Math.max(0, rentPaidAnnual - 0.1 * basicAnnual);
  return Math.max(0, Math.round(Math.min(hraReceivedAnnual, rentMinusTen, cityCap)));
}

// Supervisor is a view-only tier and cannot create any employees. Empty list
// means the create form will reject creation by a logged-in supervisor.
export const ROLE_OPTIONS_BY_LOGIN_ROLE: Record<string, string[]> = {
  admin: ["director", "founder", "hr head", "manager", "supervisor", "employee"],
  founder: ["director", "hr head", "manager", "supervisor", "employee"],
  director: ["hr head", "manager", "supervisor", "employee"],
  "hr head": ["manager", "supervisor", "employee"],
  manager: ["supervisor", "employee"],
  supervisor: [],
};

export const DESIGNATION_OPTIONS = [
  "Backend Developer",
  "Frontend Developer",
  "Full Stack Developer",
  "DevOps Engineer",
  "Data Scientist",
  "QA Engineer",
  "UI/UX Designer",
  "Product Manager",
  "HR Manager",
  "Sales Executive",
  "Marketing Manager",
  "Accountant",
];

// Helper functions
export function calcPT(grossMonthly: number, stateKey: string): number {
  const cfg = PT_STATES[stateKey] || PT_STATES.none;
  for (const [lo, hi, amt] of cfg.slabs) {
    if (grossMonthly >= lo && grossMonthly <= hi) return amt;
  }
  return 0;
}

export function lwfMonthly(stateKey: string): { ee: number; er: number } {
  const s = LWF_STATES[stateKey] || LWF_STATES.none;
  if (s.freq === "monthly") return { ee: s.ee, er: s.er };
  if (s.freq === "halfyearly") return { ee: +(s.ee / 6).toFixed(2), er: +(s.er / 6).toFixed(2) };
  if (s.freq === "annual") return { ee: +(s.ee / 12).toFixed(2), er: +(s.er / 12).toFixed(2) };
  return { ee: 0, er: 0 };
}

export function fmt(n: number | string): string {
  const num = typeof n === "string" ? parseFloat(n) : n;
  if (isNaN(num)) return "0.00";
  return new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num || 0);
}

export function num(v: any): number {
  return parseFloat(v) || 0;
}

/**
 * Display-only normalization for a person name. The first character of every
 * whitespace-separated word becomes uppercase; all other characters become
 * lowercase. So "JOHN", "john", "JoHn" all render as "John".
 *
 * The stored value is left untouched — this only formats what the UI shows.
 */
export function toProperCase(value?: string | null): string {
  if (!value) return "";
  return String(value)
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * Capitalize ONLY the very first character of a string while preserving
 * every other character exactly as the user typed it. Used on name input
 * fields so a stray lowercase first letter is auto-corrected, but later
 * casing the user chose (e.g. "CK", "McDonald", "JoHn") survives untouched.
 *
 *   capitalizeFirstLetter("ck")     === "Ck"
 *   capitalizeFirstLetter("cK")     === "CK"
 *   capitalizeFirstLetter("CK")     === "CK"
 *   capitalizeFirstLetter("JoHn")   === "JoHn"
 *   capitalizeFirstLetter("")       === ""
 */
export function capitalizeFirstLetter(value: string): string {
  if (!value) return "";
  return value.charAt(0).toUpperCase() + value.slice(1);
}
